'use client';

import { memo, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check } from 'lucide-react';

/* Robust copy — navigator.clipboard is undefined outside secure contexts (http over
   LAN/Tailscale) and its promise can reject silently. Fallback: hidden textarea +
   execCommand. Only report success when a copy actually happened (Boss 2026-07-13:
   'I click copy and it's not working' — the check mark was lying). */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

function CodeBlock({ children, className }: { children: React.ReactNode; className?: string }) {
  const [copied, setCopied] = useState<boolean | 'fail'>(false);
  const lang = className?.replace('language-', '') || '';
  const text = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    const ok = await copyText(text);
    setCopied(ok ? true : 'fail');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-3">
      {lang && (
        <div className="flex items-center justify-between px-3 py-1.5 rounded-t" style={{ backgroundColor: '#1e2028' }}>
          <span style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: '#5c6370', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lang}</span>
          <button onClick={handleCopy} className="transition-colors flex items-center gap-1" style={{ color: '#5c6370' }} onMouseEnter={e => e.currentTarget.style.color = '#abb2bf'} onMouseLeave={e => e.currentTarget.style.color = '#5c6370'}>
            {copied === 'fail' && <span style={{ fontSize: 10, color: '#e06c75' }}>select manually</span>}
            {copied === true ? <Check className="w-3 h-3" style={{ color: '#98c379' }} /> : <Copy className="w-3 h-3" />}
          </button>
        </div>
      )}
      <pre className={`${lang ? 'rounded-b rounded-t-none' : 'rounded'} px-3 py-2.5 overflow-x-auto`} style={{ backgroundColor: '#1a1a2e' }}>
        <code style={{ fontSize: '13px', lineHeight: '1.6', fontFamily: 'var(--font-mono)', color: '#abb2bf' }}>{text}</code>
      </pre>
      {!lang && (
        <button onClick={handleCopy} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1" style={{ color: '#5c6370' }}>
          {copied === 'fail' && <span style={{ fontSize: 10, color: '#e06c75' }}>select manually</span>}
          {copied === true ? <Check className="w-3 h-3" style={{ color: '#98c379' }} /> : <Copy className="w-3 h-3" />}
        </button>
      )}
    </div>
  );
}

// Cursor-matched colors
// eslint-disable-next-line @typescript-eslint/no-explicit-any

/* Click an image -> it expands in place, over the chat. Not a new tab (Boss 2026-09-18:
   "expand images in the chat, same as the comic software"). Plain DOM rather than React
   state so the markdown component map, which lives outside the React tree, can call it.
   Esc or a click closes it; the arrow keys walk every image in the conversation. */
function openLightbox(startUrl: string) {
  const shots = Array.from(document.querySelectorAll<HTMLImageElement>('img[data-chat-img]'));
  let i = Math.max(0, shots.findIndex((n) => n.getAttribute('data-full') === startUrl));

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'position:fixed;inset:0;z-index:9999;background:rgba(8,9,13,.94);display:flex;' +
    'align-items:center;justify-content:center;flex-direction:column;gap:10px;cursor:zoom-out';
  const big = document.createElement('img');
  big.style.cssText = 'max-width:94vw;max-height:88vh;object-fit:contain;border-radius:8px;box-shadow:0 10px 60px rgba(0,0,0,.6)';
  const cap = document.createElement('div');
  cap.style.cssText = 'font:12px var(--font-mono,monospace);color:#8b93a3;text-align:center;max-width:90vw';
  wrap.append(big, cap);

  const show = (n: number) => {
    i = (n + shots.length) % shots.length;
    const el = shots[i];
    big.src = el.getAttribute('data-full') || el.src;
    const alt = el.getAttribute('alt') || '';
    cap.textContent = shots.length > 1 ? `${alt}${alt ? '  ·  ' : ''}${i + 1} / ${shots.length}  ·  ← →` : alt;
  };
  const close = () => {
    wrap.remove();
    document.removeEventListener('keydown', key);
  };
  const key = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); show(i - 1); }
  };
  wrap.addEventListener('click', close);
  big.addEventListener('click', (e) => { e.stopPropagation(); show(i + 1); });
  document.addEventListener('keydown', key);
  show(i);
  document.body.appendChild(wrap);
}

const mdComponents: Record<string, React.ComponentType<any>> = {
  h1: ({ children }: { children?: React.ReactNode }) => <h1 style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #2a2a3a', paddingBottom: '4px' }}>{children}</h1>,
  h2: ({ children }: { children?: React.ReactNode }) => <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', marginTop: '12px', marginBottom: '6px' }}>{children}</h2>,
  h3: ({ children }: { children?: React.ReactNode }) => <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#d4d4d4', marginTop: '10px', marginBottom: '4px' }}>{children}</h3>,
  p: ({ children }: { children?: React.ReactNode }) => <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#d4d4d4', margin: '6px 0' }}>{children}</p>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong style={{ fontWeight: 600, color: '#ffffff' }}>{children}</strong>,
  em: ({ children }: { children?: React.ReactNode }) => <em style={{ color: '#9ca3af', fontStyle: 'italic' }}>{children}</em>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    /* cursor://file/abs/path or a bare local file link → server opens it in Cursor
       (Boss 2026-07-12: .md links must land in Cursor; browsers swallow cursor://) */
    const local = href?.startsWith('cursor://file') ? href.slice('cursor://file'.length)
      : href?.startsWith('file://') ? href.slice('file://'.length)
      : href?.startsWith('/Users/') ? href : null;
    if (local) {
      return (
        <a href="#" onClick={(e) => { e.preventDefault(); fetch('/api/open-in-editor', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: decodeURIComponent(local) }) }); }}
          style={{ color: '#9ef0ff', textDecoration: 'underline', textUnderlineOffset: '2px', cursor: 'pointer' }}>{children}</a>
      );
    }
    return (
      <a href={href} style={{ color: '#7ec699', textDecoration: 'underline', textUnderlineOffset: '2px' }} target="_blank" rel="noopener noreferrer">{children}</a>
    );
  },
  /* Inline image preview. Local absolute paths (file://, /Users/…, /tmp/…) are
     served by /api/local-image; http(s)/data URLs pass through. Small preview,
     click → full size in a new tab. (Boss 2026-09-06: 'make the images open right
     in the chat') */
  img: ({ src, alt }: { src?: string; alt?: string }) => <ChatImage src={src} alt={alt} />,
  ul: ({ children }: { children?: React.ReactNode }) => <ul style={{ margin: '6px 0', marginLeft: '16px', listStyleType: 'disc' }}>{children}</ul>,
  // (ChatImage lives at the bottom of this file — it needs hooks, so it cannot
  //  be an inline arrow inside this plain object.)
  ol: ({ children }: { children?: React.ReactNode }) => <ol style={{ margin: '6px 0', marginLeft: '16px', listStyleType: 'decimal' }}>{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li style={{ fontSize: '14px', lineHeight: '1.65', color: '#d4d4d4', paddingLeft: '2px', marginBottom: '2px' }}>{children}</li>,
  blockquote: ({ children }: { children?: React.ReactNode }) => <blockquote style={{ borderLeft: '2px solid #3a5a3a', paddingLeft: '12px', margin: '8px 0', color: '#9ca3af', fontStyle: 'italic' }}>{children}</blockquote>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #2a2a3a', margin: '12px 0' }} />,
  table: ({ children }: { children?: React.ReactNode }) => <div style={{ margin: '8px 0', overflowX: 'auto' }}><table style={{ fontSize: '12px', borderCollapse: 'collapse', width: '100%' }}>{children}</table></div>,
  thead: ({ children }: { children?: React.ReactNode }) => <thead style={{ backgroundColor: '#1e2028' }}>{children}</thead>,
  th: ({ children }: { children?: React.ReactNode }) => <th style={{ border: '1px solid #2a2a3a', padding: '6px 8px', textAlign: 'left', color: '#d4d4d4', fontWeight: 500 }}>{children}</th>,
  td: ({ children }: { children?: React.ReactNode }) => <td style={{ border: '1px solid #2a2a3a', padding: '6px 8px', color: '#abb2bf' }}>{children}</td>,
  code: ({ className, children, ...props }: { className?: string; children?: React.ReactNode }) => {
    if (className?.startsWith('language-')) {
      return <CodeBlock className={className}>{children}</CodeBlock>;
    }
    return <code style={{ fontSize: '13px', fontFamily: 'var(--font-mono)', backgroundColor: '#2a2a3a', color: '#e5c07b', padding: '1px 6px', borderRadius: '3px', overflowWrap: 'anywhere' }} {...props}>{children}</code>;
  },
  pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
};

const remarkPlugins = [remarkGfm];

/* Which absolute paths the chat treats as a file on disk rather than a web URL.
   2026-09-17: this was `/Users/` only, so `![x](/tmp/crop.png)` — the path agents
   actually use for throwaway crops — was stripped by defaultUrlTransform and
   rendered as a broken image. `/api/…` and other app-relative URLs must NOT match,
   so this is an explicit root list, not a bare leading-slash test. The same roots
   are enforced server-side in /api/local-image. */
const LOCAL_ROOTS = ['/Users/', '/tmp/', '/private/tmp/', '/var/folders/', '/private/var/folders/', '/home/'];

export function isLocalImagePath(url: string) {
  return url.startsWith('file://') || LOCAL_ROOTS.some((r) => url.startsWith(r));
}

function localImagePath(src: string): string | null {
  if (src.startsWith('file://')) return decodeURIComponent(src.slice('file://'.length));
  return LOCAL_ROOTS.some((r) => src.startsWith(r)) ? decodeURIComponent(src) : null;
}

/* An image in the chat, with a delete affordance.

   Boss 2026-09-19: "i need ability to delete image generated right now i dont
   have this ability." Generation produces misses, and every miss stayed on
   disk forever because the only view of it was a read-only preview.

   The × TRASHES: the server renames the file to _trash-YYYY-MM-DD-<name> in
   place. Nothing is erased, so a mis-click costs nothing, and the tile says so
   afterwards. Only local files can be trashed — a remote URL has no file to
   move, so the button is not shown. */
function ChatImage({ src, alt }: { src?: string; alt?: string }) {
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!src) return null;
  const local = localImagePath(src);
  const url = local ? `/api/local-image?path=${encodeURIComponent(local)}` : src;

  const trash = async (e: React.MouseEvent) => {
    e.stopPropagation();           // never open the lightbox on a delete click
    if (!local || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/api/local-image?path=${encodeURIComponent(local)}`, { method: 'DELETE' })
        .then(x => x.json());
      if (r?.ok) setGone(true); else setErr(r?.error || 'delete failed');
    } catch (e2: any) { setErr(e2?.message || 'delete failed'); }
    setBusy(false);
  };

  if (gone) {
    return (
      <span style={{ display: 'inline-block', margin: '6px 8px 6px 0', verticalAlign: 'top',
                     padding: '10px 12px', borderRadius: 6, border: '1px dashed #3a3a4a',
                     background: '#12141b', fontSize: 11, color: '#8b93a3',
                     fontFamily: 'var(--font-mono)' }}>
        trashed — renamed to _trash-… on disk, not erased
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-block', margin: '6px 8px 6px 0', verticalAlign: 'top', position: 'relative' }}>
      <img src={url} alt={alt || ''} loading="lazy" data-chat-img data-full={url}
        title={local || src}
        onClick={() => openLightbox(url)}
        style={{ maxHeight: 220, maxWidth: '100%', borderRadius: 6, border: '1px solid #2a2a3a', display: 'block', objectFit: 'contain', background: '#0f1117', cursor: 'zoom-in' }} />
      {local && (
        <button onClick={trash} disabled={busy} title="Delete this image (moves it to _trash-, does not erase)"
          style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, lineHeight: '20px',
                   borderRadius: 5, border: '1px solid #4a2a2a', background: 'rgba(20,10,10,.82)',
                   color: busy ? '#6b7280' : '#f87171', fontSize: 14, cursor: busy ? 'default' : 'pointer',
                   padding: 0, textAlign: 'center' }}>×</button>
      )}
      {alt && <span style={{ display: 'block', fontSize: 11, color: '#8b93a3', marginTop: 3, fontFamily: 'var(--font-mono)' }}>{alt}</span>}
      {err && <span style={{ display: 'block', fontSize: 11, color: '#f87171', marginTop: 3 }}>{err}</span>}
    </span>
  );
}

export const MarkdownBlock = memo(function MarkdownBlock({ text }: { text: string }) {
  return (
    <div className="break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0" style={{ overflowWrap: 'anywhere' }}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={mdComponents}
        urlTransform={(url) => (url.startsWith('cursor://') || isLocalImagePath(url) ? url : defaultUrlTransform(url))}>
        {text}
      </ReactMarkdown>
    </div>
  );
});
